import { useEffect, useState } from 'react';
import { ConnectionState } from 'livekit-client';
import {
  LiveKitRoom,
  RoomAudioRenderer,
  StartAudio,
  useConnectionState,
  useLocalParticipant,
  useRemoteParticipants,
  useRoomContext,
} from '@livekit/components-react';
import '@livekit/components-styles';
import { Mic, MicOff, PhoneOff, Loader2, AlertCircle, ExternalLink } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { ConversationView, downloadTranscriptAsText } from '@/components/custom/conversation-view';
import { useLiveTranscript } from '@/hooks/use-live-transcript';
import type { StartCallResponse } from '@vagusx/clients';
import { useTestCall, type TestCallStartOptions } from './use-test-call';
import { useStartOnce } from './use-start-once';

interface CallPanelProps {
  /**
   * Call options for the FIRST start. **Subsequent prop changes are
   * ignored** — the start effect runs once per component instance, so the
   * idempotencyKey stays stable across a StrictMode double-mount.
   *
   * To start a different call (different idempotencyKey, different
   * callType, different overrides) the parent MUST remount this
   * component by giving it a new `key` prop. Mutating `options` in
   * place will leave the component running the original call silently.
   * See `ScheduleAppointmentDrawer.tsx` for the canonical pattern
   * (`<CallPanel key={callKey} ... />` with `setCallKey(k => k + 1)`
   * on each fresh start).
   */
  options?: TestCallStartOptions;
  /**
   * Instead of `options`: start the call from another endpoint that answers
   * like `/browser-call/test` — the workflow builder's draft tests. Read
   * once, like `options`.
   */
  startCall?: () => Promise<StartCallResponse>;
  /** Shown above the call. */
  banner?: React.ReactNode;
  /** Fired when the call ends (user hung up, or the remote disconnected).
   * The interactionId is passed back so the parent can surface a link to
   * the post-call view. `null` if the call ended before a token was ever
   * fetched. */
  onEnd: (interactionId: string | null) => void;
  /**
   * SIP-outbound only. Human label for the party being dialed (e.g.
   * "+1 760-900-5114") shown in the monitor header. Ignored in
   * browser-test (participant) mode where the operator IS the peer.
   */
  peerLabel?: string;
}

export function CallPanel({
  options,
  startCall,
  banner,
  onEnd,
  peerLabel,
}: CallPanelProps): React.ReactElement {
  const call = useTestCall();

  // Once per component INSTANCE — see CallPanelProps.options and the incident
  // recorded in `useStartOnce`. Starting the call is irreversible (it rings a
  // patient's phone), so it must not ride on `useEffect(fn, [])`, which
  // StrictMode runs twice.
  useStartOnce(() => {
    if (startCall) void call.startWith(startCall);
    else if (options) void call.start(options);
  });

  if (call.phase === 'error') {
    return (
      <div className="flex h-full flex-col">
        <div className="flex items-center justify-between border-b bg-destructive/10 px-4 py-2 text-sm text-destructive">
          <span className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            {call.errorMessage ?? 'Failed to connect.'}
          </span>
          <Button variant="outline" size="sm" onClick={() => onEnd(null)}>
            Back
          </Button>
        </div>
      </div>
    );
  }

  if (call.phase !== 'ready' || !call.token || !call.serverUrl) {
    // SIP places a real call here: the API blocks until the callee answers
    // (waitUntilAnswered), so this can sit for many seconds. Give the
    // operator a clear "placing" message and a way out instead of an
    // affordance-less spinner. Note: backing out stops monitoring but
    // cannot recall a call already placed on the trunk.
    const isSip = options?.transport === 'sip-outbound';
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
        <span className="flex items-center">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          {isSip ? 'Placing call — waiting for the line to connect…' : 'Connecting…'}
        </span>
        <Button variant="outline" size="sm" onClick={() => onEnd(null)}>
          {isSip ? 'Stop waiting' : 'Cancel'}
        </Button>
      </div>
    );
  }

  // Viewer (SIP) mode: subscribe-only. The viewer token can't publish,
  // and there's no human on this end — the patient talks to the agent
  // over the phone. Publishing the mic here would error on the token
  // grant, so audio-publish is off and the panel renders monitor-only.
  const isMonitor = call.mode === 'viewer';

  return (
    <LiveKitRoom
      token={call.token}
      serverUrl={call.serverUrl}
      audio={!isMonitor}
      video={false}
      connect={true}
      onDisconnected={() => {
        // Single source of truth for "the call ended". Whether the user
        // clicked End call (which calls room.disconnect()), the agent
        // hung up, or the connection dropped — we end here.
        onEnd(call.interactionId);
      }}
      onError={(err) => console.error('[CallPanel] LiveKit room error:', err)}
      className="flex h-full flex-col"
    >
      <RoomAudioRenderer />
      {banner}
      <CallContents
        interactionId={call.interactionId}
        isMonitor={isMonitor}
        peerLabel={peerLabel}
      />
    </LiveKitRoom>
  );
}

function CallContents({
  interactionId,
  isMonitor,
  peerLabel,
}: {
  interactionId: string | null;
  isMonitor: boolean;
  peerLabel?: string;
}): React.ReactElement {
  const connState = useConnectionState();
  const { localParticipant, isMicrophoneEnabled } = useLocalParticipant();
  const remoteParticipants = useRemoteParticipants();
  const room = useRoomContext();
  const [showToolCalls, setShowToolCalls] = useState(true);
  const [durationMs, setDurationMs] = useState(0);
  const [agentJoinError, setAgentJoinError] = useState<string | null>(null);

  // Browser side has finished WebRTC negotiation with LiveKit Cloud.
  // This alone is NOT "the call has started" from the user's perspective
  // — the agent worker can still be cold-starting on Cloud Run and not
  // yet present in the room.
  const connected = connState === ConnectionState.Connected;
  // "Agent present" must exclude the SIP callee. In monitor mode the room
  // also contains the dialed party (identity `caller-<fromPhone>`, set by
  // the LiveKit outbound service); counting them as the agent would make
  // the agent-join watchdog never fire and show "Live" with no agent.
  // Browser-test rooms have no `caller-*` participant, so this is a no-op
  // there.
  const hasAgent = remoteParticipants.some((p) => !p.identity.startsWith('caller-'));
  // The call is actually live only when both sides are in the room.
  // Use this for the "Live" indicator, the duration timer, and anything
  // the user reads as "the call has started."
  const isLive = connected && hasAgent;

  useEffect(() => {
    if (!isLive) return;
    const startedAt = Date.now();
    const iv = setInterval(() => setDurationMs(Date.now() - startedAt), 500);
    return () => clearInterval(iv);
  }, [isLive]);

  // Agent-joined watchdog. The /test endpoint mints a token and creates
  // the LiveKit room, but the agent worker on Cloud Run can take 15–30s
  // to scale from zero and register before it picks up the dispatch.
  // While that's happening, `connected` is true but `hasAgent` is false
  // — the UI shows "Waiting for agent…" instead of lying about being
  // live. If the agent still hasn't joined after 45s, the worker is
  // probably down or misconfigured (not just cold-starting); surface a
  // real error so the tester knows to retry rather than wait forever.
  useEffect(() => {
    if (!connected || hasAgent || agentJoinError) return;
    const timer = setTimeout(() => {
      setAgentJoinError(
        'Agent did not join the room after 45 seconds. The LiveKit worker may be down or unable to dispatch — check worker logs.'
      );
    }, 45000);
    return () => clearTimeout(timer);
  }, [connected, hasAgent, agentJoinError]);

  const { messages, connectionState: sseState } = useLiveTranscript(
    interactionId ?? undefined,
    isLive
  );

  const handleEnd = async (): Promise<void> => {
    // Single path to end the call: disconnect the room. LiveKitRoom's
    // onDisconnected handler then fires and invokes the parent's onEnd
    // with the interactionId, which triggers the CallEnded screen.
    //
    // Monitor mode caveat: the operator has no mic, and disconnecting
    // only removes the *viewer* — the agent and the patient stay on the
    // live SIP call. So we skip the mic toggle and treat this as "stop
    // monitoring", not "hang up".
    if (!isMonitor) {
      try {
        await localParticipant.setMicrophoneEnabled(false);
      } catch (err) {
        console.warn('[CallPanel] mic toggle failed:', err);
      }
    }
    try {
      await room.disconnect();
    } catch (err) {
      console.error('[CallPanel] room.disconnect failed:', err);
    }
  };

  const toggleMute = async (): Promise<void> => {
    await localParticipant.setMicrophoneEnabled(!isMicrophoneEnabled);
  };

  return (
    <>
      {agentJoinError && (
        <div className="flex items-start gap-2 border-b border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span className="flex-1">{agentJoinError}</span>
        </div>
      )}
      <div className="flex items-center justify-between gap-3 border-b bg-muted/30 px-4 py-2 text-sm">
        <div className="flex items-center gap-2">
          <StateIcon state={connState} hasAgent={hasAgent} />
          <span className="font-medium">{stateLabel(connState, hasAgent)}</span>
          {isMonitor && peerLabel && (
            <span className="text-muted-foreground">· calling {peerLabel}</span>
          )}
          {isLive && (
            <span className="text-muted-foreground tabular-nums">
              · {formatDuration(durationMs)}
            </span>
          )}
          {interactionId && (
            <Link
              to="/activity/$interactionId"
              params={{ interactionId }}
              className="ml-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <ExternalLink className="h-3 w-3" />
              Post-call view
            </Link>
          )}
        </div>

        <div className="flex items-center gap-2">
          <StartAudio
            label="Click to enable audio"
            className="inline-flex h-8 items-center gap-1.5 rounded-md border bg-warning/20 px-3 text-xs text-warning-foreground hover:bg-warning/30"
          />
          {/* Mic controls only in browser-test mode — in monitor mode the
              operator has no mic (the patient talks over the phone). */}
          {!isMonitor && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1.5"
              onClick={() => void toggleMute()}
              disabled={!connected}
            >
              {isMicrophoneEnabled ? (
                <Mic className="h-3.5 w-3.5" />
              ) : (
                <MicOff className="h-3.5 w-3.5" />
              )}
              {isMicrophoneEnabled ? 'Mute' : 'Unmute'}
            </Button>
          )}
          <Button
            variant="destructive"
            size="sm"
            className="h-8 gap-1.5"
            onClick={() => void handleEnd()}
          >
            <PhoneOff className="h-3.5 w-3.5" />
            {isMonitor ? 'Stop monitoring' : 'End call'}
          </Button>
        </div>
      </div>

      {/* `flex flex-col` is load-bearing: ConversationView's root is
          `flex min-h-0 flex-1 flex-col`, but `flex-1` only resolves
          inside a flex parent. Without `flex flex-col` here the
          transcript scroller can't bound its height and messages
          render past the viewport (no scroll, no auto-follow). */}
      <div className="flex min-h-0 flex-1 flex-col">
        <ConversationView
          messages={messages}
          isLive={isLive}
          connectionState={sseState}
          showToolCalls={showToolCalls}
          header={{
            showToolCalls: { value: showToolCalls, onChange: setShowToolCalls },
            onDownload:
              messages.length > 0
                ? () =>
                    downloadTranscriptAsText(
                      messages,
                      `test-call-${interactionId ?? 'session'}.txt`
                    )
                : undefined,
          }}
          emptyState={
            !connected
              ? 'Connecting…'
              : !hasAgent
                ? isMonitor
                  ? 'Ringing… waiting for the call to connect.'
                  : 'Waiting for agent to join…'
                : isMonitor
                  ? 'Live call in progress — transcript will appear here.'
                  : 'Say something to get the agent going.'
          }
        />
      </div>
    </>
  );
}

/**
 * Pure variant of the status icon — returns which icon shape to render,
 * not the JSX itself. Exported so it's unit-testable as a pure function;
 * the visual mapping below is just a switch on the result.
 *
 * The non-obvious case: `Connected` + no agent reads as "still
 * connecting" to the user — the call hasn't actually started until the
 * agent is in the room. So we render the spinner there too, NOT the red
 * pulsing dot.
 */
export type StateIconKind = 'spinner' | 'live-pulse' | 'idle';

export function stateIconKind(state: ConnectionState, hasAgent: boolean): StateIconKind {
  if (state === ConnectionState.Connecting || state === ConnectionState.Reconnecting) {
    return 'spinner';
  }
  if (state === ConnectionState.Connected && !hasAgent) {
    return 'spinner';
  }
  if (state === ConnectionState.Connected) {
    return 'live-pulse';
  }
  return 'idle';
}

function StateIcon({
  state,
  hasAgent,
}: {
  state: ConnectionState;
  hasAgent: boolean;
}): React.ReactElement {
  const kind = stateIconKind(state, hasAgent);
  if (kind === 'spinner') return <Loader2 className="h-3.5 w-3.5 animate-spin" />;
  if (kind === 'live-pulse')
    return <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-destructive" />;
  return <span className="inline-block h-2 w-2 rounded-full bg-muted-foreground/40" />;
}

/**
 * Status label shown next to the state icon. Exported for unit
 * testing — the connected-but-no-agent case is the one we're guarding
 * against (used to read "Live" + start a clock; now reads "Waiting for
 * agent" until the agent participant actually joins).
 */
export function stateLabel(state: ConnectionState, hasAgent: boolean): string {
  switch (state) {
    case ConnectionState.Connecting:
      return 'Connecting';
    case ConnectionState.Connected:
      return hasAgent ? 'Live' : 'Waiting for agent';
    case ConnectionState.Reconnecting:
      return 'Reconnecting';
    case ConnectionState.Disconnected:
      return 'Disconnected';
    default:
      return 'Idle';
  }
}

function formatDuration(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${sec.toString().padStart(2, '0')}`;
}
